import {Component, Input, OnInit, signal, ViewChild} from '@angular/core';
import {GraphModel} from '../models/graph.model';
import * as shape from 'd3';
import {GraphComponent} from '@swimlane/ngx-graph';
import {BaseModalComponent, LeModalService, PropertiesQuery} from 'common-shared';
import {NgbPopover} from "@ng-bootstrap/ng-bootstrap";
import {DagreNodesOnlyLayout, Orientation} from "./DagreNodesOnlyLayout";
import {Observable, Subject} from "rxjs";
import {GraphNodeModel} from "../models/graph-node.model";
import {GraphLinkModel} from "../models/graph-link.model";
import {IntegratedMasterService} from "../../integrated-master-editor/services/integrated-master.service";
import {ItemService} from "../../../common/item/item.service";
import {ManufacturingItemEditModalComponent} from "../../manufacturing-item/manufacturing-item-edit-modal/manufacturing-item-edit-modal.component";
import {ManufacturingItemGroupEditModalComponent} from "../../manufactured-item-groups/manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {ResourceEditModalComponent} from "../../resources/resource-edit-modal/resource-edit-modal.component";
import {GraphService} from "../graph.service";
import {GraphOptionsComponent} from "../graph-options/graph-options.component";
import {EntityTypes} from "web-shared";
import SvgSaver from 'svgsaver'
import {GraphOptions} from "../models/graph-options";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'mes-web-manufacturing-graph-modal',
  templateUrl: './manufacturing-graph-modal.component.html',
  styleUrls: ['./manufacturing-graph-modal.component.scss']
})
export class ManufacturingGraphModalComponent extends BaseModalComponent implements OnInit {
  title: string;
  id: string;
  graph = signal<GraphModel>(null);
  graphSource: 'Item' | 'ItemGroup' | 'Resource' | 'AllItems' = 'Item';
  hiddenBranches: { Id: string, Nodes: GraphNodeModel[], Links: GraphLinkModel[] }[] = [];
  sharedNodes: GraphNodeModel[] = [];
  layout: any;
  orientation?: string;
  curve?: any = shape.curveBundle.beta(1);
  @ViewChild(GraphComponent, {static: true}) chart: GraphComponent;
  update$: Subject<boolean> = new Subject();
  @Input() options: GraphOptions = null;

  constructor(public propertiesQuery: PropertiesQuery, private modalService: LeModalService, private integratedMasterService: IntegratedMasterService, public itemService: ItemService, private graphService: GraphService) {
    super();
    const layout = new DagreNodesOnlyLayout();
    layout.defaultSettings = {
      orientation: Orientation.LEFT_TO_RIGHT,
      marginX: 20,
      marginY: 20,
      edgePadding: 10,
      rankPadding: 60,
      nodePadding: 30,
      curveDistance: 20
    };

    this.layout ??= layout;
  }

  ngOnInit() {
    this.load();
  }

  viewObjectUnderNode(node: GraphNodeModel) {
    if ([EntityTypes.MasterInputInstruction, EntityTypes.MasterOutputInstruction, EntityTypes.General, EntityTypes.IntermediateItem].includes(node.type)) {
      if (node.Data.ItemId) {
        this.showManufacturingItemEditModal(node.Data.ItemId);
      } else if (node.Data.ItemGroupId) {
        this.showManufacturedItemGroupEditModal(node.Data.ItemGroupId)
      }
    } else if (node.type === EntityTypes.MasterUseInstruction) {
      if (node.Data.ItemId) {
        this.showManufacturingItemEditModal(node.Data.ItemId);
      } else if (node.Data.ItemGroupId) {
        this.showManufacturedItemGroupEditModal(node.Data.ItemGroupId)
      }
    } else if ([EntityTypes.MasterOperation, EntityTypes.ProcessSelector, EntityTypes.TaskSelector].includes(node.type)) {
      this.showImeModal(node);
    } else if ([EntityTypes.Item, EntityTypes.Material].includes(node.type)) {
      this.showManufacturingItemEditModal(node.entityId);
    } else if (node.type === EntityTypes.Resource) {
      this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: node.Data.ResourceId} as ResourceEditModalComponent, class: 'modal-xl'});
    } else if (node.type === EntityTypes.ItemGroup) {
      this.showManufacturedItemGroupEditModal(node.entityId)
    }
  }

  private showManufacturingItemEditModal(entityId: string): void {
    this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: entityId} as ManufacturingItemEditModalComponent, class: 'modal-xl', ignoreBackdropClick: true});
  }

  private showManufacturedItemGroupEditModal(entityId: string): void {
    this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: entityId} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl', ignoreBackdropClick: true});
  }

  private showImeModal(node: GraphNodeModel): void {
    if (node.Data.ItemGroupId) {
      this.modalService.show(ManufacturingItemGroupEditModalComponent, {
        initialState: {entityId: node.Data.ItemGroupId, currentTab: 'ime', scrollIntoViewId: node.entityId} as ManufacturingItemGroupEditModalComponent,
        class: 'modal-xl',
        ignoreBackdropClick: true
      });
    } else {
      this.modalService.show(ManufacturingItemEditModalComponent, {
        initialState: {entityId: node.Data.ItemId, currentTab: 'ime', scrollIntoViewId: node.entityId} as ManufacturingItemEditModalComponent,
        class: 'modal-xl',
        ignoreBackdropClick: true
      });
    }
  }

  openGraph(node: GraphNodeModel) {
    const id = node.isMasterOutputInstructionNode() ? node.Data.ItemId : node.entityId;

    this.modalService.show(ManufacturingGraphModalComponent,
      {
        initialState: {
          title: node.label, id: id, graphSource: node.isItemGroupNode() ? "ItemGroup" : "Item"
        } as ManufacturingGraphModalComponent, class: 'modal-full'
      });
  }

  togglePopover(popover: NgbPopover, node: GraphNodeModel, link?: GraphLinkModel) {
    if (popover.isOpen()) {
      popover.close();
    } else {
      popover.open({
        close: () => {
          popover.close();
        }, node: node, link: link
      });
    }
  }

  toggleBranch(node: GraphNodeModel): void {
    if (this.graph().Links.some(link => link.target === node.id)) {
      this.hideBranch(node.id);
    } else {
      this.showBranch(node.id);
    }
  }

  private hideBranch(sourceNodeId): void {
    const linksToHide = this.getLinksToHide(sourceNodeId);
    const nodeIdsToHide = linksToHide.map(link => link.source).filter(nodeId => this.hasSingleSource(nodeId, linksToHide));
    const nodesToHide = this.graph().Nodes.filter(node => nodeIdsToHide.includes(node.id));

    this.graph.update(value => {
      value.Nodes = value.Nodes.filter(node => !nodeIdsToHide.includes(node.id));
      value.Links = value.Links.filter(link => !linksToHide.includes(link));
      return value;
    });

    this.hiddenBranches.push({Id: sourceNodeId, Nodes: nodesToHide, Links: linksToHide});
  }

  private showBranch(sourceNodeId): void {
    const hiddenBranch = this.hiddenBranches.find(branch => branch.Id === sourceNodeId);
    this.graph.update(value => {
      value.Nodes.push(...hiddenBranch.Nodes);
      value.Links.push(...hiddenBranch.Links);
      return value;
    })
    this.ensureSharedNodes(hiddenBranch.Links);
    this.update$.next(true);
    this.hiddenBranches = this.hiddenBranches.filter(branch => branch.Id !== sourceNodeId);
  }

  getLinksToHide(targetId: string, nextLinksToCheck: GraphLinkModel[] = [], linksToHide: GraphLinkModel[] = []): GraphLinkModel[] {
    const sourceLinks = this.graph().Links.filter(link => link.target === targetId);
    linksToHide = [...linksToHide, ...sourceLinks];
    if (sourceLinks.length) {
      for (let link of sourceLinks) {
        nextLinksToCheck = [...nextLinksToCheck, ...sourceLinks.filter(link => !nextLinksToCheck.includes(link))]
        nextLinksToCheck = nextLinksToCheck.filter(toCheck => toCheck !== link);
        this.getLinksToHide(link.source, nextLinksToCheck, linksToHide);
      }
      return linksToHide;
    } else if (nextLinksToCheck.length) {
      const nextLink = nextLinksToCheck.shift();
      return this.getLinksToHide(nextLink.source, nextLinksToCheck, linksToHide);
    } else {
      return linksToHide;
    }
  }

  private hasSingleSource(nodeId: string, linksToHide: GraphLinkModel[]): boolean {
    const totalLinksFromNode = this.graph().Links.filter(link => link.source === nodeId);
    const linksFromOtherBranches = totalLinksFromNode.filter(link => !linksToHide.includes(link));
    if (linksFromOtherBranches.length === 0) {
      return true;
    }
    const sharedNode = this.graph().Nodes.find(node => node.id === nodeId);
    if (!this.sharedNodes.includes(sharedNode)) {
      this.sharedNodes.push(sharedNode);
    }
    return false;
  }

  private ensureSharedNodes(hiddenLinks: GraphLinkModel[]): void {
    hiddenLinks.forEach(link => {
      const sharedNode = this.sharedNodes.find(node => node.id === link.source);
      if (sharedNode && !this.graph().Nodes.includes(sharedNode)) {
        this.graph().Nodes.push(sharedNode);
        this.sharedNodes = this.sharedNodes.filter(node => node !== sharedNode);
      }
    });
  }

  showGraphOptions() {
    this.modalService.show(GraphOptionsComponent, {
      initialState: {
        options: this.options
      }
    }).onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe((o) => {
      if (o.result == 'apply') {
        this.options = o.data;
        this.load();
      }
    });
  }

  private load() {
    let load$: Observable<GraphModel>;
    switch (this.graphSource) {
      case "Item":
        load$ = this.graphService.getItemGraph(this.id, this.options);
        break;
      case "ItemGroup":
        load$ = this.graphService.getItemGroupGraph(this.id, this.options);
        break;
      case "Resource":
        load$ = this.graphService.getResourceGraph(this.id, this.options);
        break;
      case "AllItems":
        load$ = this.graphService.getAllFinishedItemsGraph(this.options);
        break;
    }

    load$.pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.graph.set(value);
      this.title ??= this.graph().Nodes.find(value => value.id === this.graph().Id).label;
    });
  }

  exportAs(format: 'SVG' | 'PNG') {
    const graphContainer = document.getElementById(`graph-container-${this.id}`);
    const graph = graphContainer?.getElementsByTagName('svg')[0];
    const svgSaver = new SvgSaver();
    const name = `${this.title.replace(':', '').split(' ').join('_')}.${format.toLowerCase()}`;
    if (format === 'SVG') {
      svgSaver.asSvg(graph, name);
    } else if (format === 'PNG') {
      svgSaver.asPng(graph, name);
    }
  }
}
